<template>
    <Widget :title="$t('components.TextToPayDashboard.PaymentsOverTimeLabel')" :loading="loading" :report-mode="reportMode">
        <v-row align="center" justify="space-between" style="width: 100%">
            <v-col cols="12" sm="4">
                <span class="d-inline-block mx-4 text-caption ddwhite--text" v-for="(item, index) in Object.values(legend)" :key="index">
                    <span class="circle" :style="{ backgroundColor: item.color }"></span> 
                    <span class="label">{{ item.title }}</span>
                </span>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.Filters.GroupText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="filterBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'by_month', hint: $t('components.Filters.MonthLabel') },
                        { value: 'by_month_accumulative', hint: $t('components.Filters.MonthCumulativeLabel') },
                        { value: 'by_day_of_week', hint: $t('components.Filters.DayLabel') } ]"
                    :label="$t('components.Filters.GroupText')"
                    @change="filterData(chartData)"/>
            </v-col>
        </v-row>
        <StackedBarChart :stats="stats" :styles="styles"/>
    </Widget>
</template>

<script>
/** 
 * PaymentsOverTimeChart wraps a StackedBarChart and contains the functions to parse
 * and format data from the API.
 * 
 * It represents the number of chats versus the number of referrals grouped
 * by month, day of the week or hour of the day.
 */

import { DayLabels } from '@/mixins/TimeLabels';

import Widget from '@/components/dashboard/Widget';

import StackedBarChart from '@/components/dashboard/charts/StackedBarChart';

export default {
    name: 'PaymentsOverTimeChart',
    data: () => ({
        stats: {},
        styles: {
            position: 'relative',
            width: '100%',
            padding: '24px 24px 0',
        },
        groupBy: 'all',
        filterBy: 'by_month',
        legend: [],
    }),
    props: {
        loading: {
            type: Boolean,
            default: true
        },
        chartData: {
            type: Object,
            required: true
        },
        reportMode: {
            type: String,
            default: null
        },
    },
    computed: {
        isReport() { return this.reportMode !== null },
        colors() {
            return this.$vuetify.theme.isDark ? 
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        },
    },
    mounted() {
        this.inflateLegend();
        this.updateStats(this.chartData);
        console.log("STATS mounted");
    },
    watch: {
        chartData(newData) {
            this.updateStats(newData);
        },
        groupBy() {
            this.updateStats(this.chartData);
        },
        filterBy() {
            this.updateStats(this.chartData);
        }
    },
    methods: {
        inflateLegend() {
            const $t = this.$t.bind(this);
            this.legend = {
                payments: {
                    title: $t("components.TextToPayDashboard.AmountPaidLabel"),
                    color: this.colors.ddgreen
                },
            }
        },
        updateStats(data) {
            if (data) {
                const items = this.filterData(data);
                this.stats = this.formatItems(items);
            }
            switch (this.reportMode) {
                case 'weekly':
                    this.filterBy = 'by_day_of_week';
                    break;
                case 'monthly':
                    this.filterBy = 'by_month';
                    break;
            }
        },
        filterData(data) {
            const $t = this.$t.bind(this);
            const items = data[this.filterBy];

            if (items) {
                let formatedItems = {};
                let labels = [];

                switch (this.filterBy) {
                    case 'by_month':
                    case 'by_month_accumulative':
                        labels = Object.keys(items);
                        break;
                    case 'by_day_of_week':
                        labels = DayLabels.map(l => $t(`components.Filters.${l}`));
                        break;
                }

                const keys = Object.keys(items);
                const { length } = keys;
                for (let i = 0; i < length; i++) {
                    const key = keys[i];
                    const label = labels[i];
                    formatedItems[label] = items[key];
                }
                return formatedItems;
            }
            
            return {};
        },
        formatItems(items) {
            const $t = this.$t.bind(this);

            const labels = [...Object.keys(items)];
            const datasets = [];

            const payments = {
                barPercentage: 0.5,
                label: $t("components.TextToPayDashboard.AmountPaidLabel"),
                backgroundColor: this.colors.ddgreen,
                data: []
            }

            const { length } = labels;
            for (let i = 0; i < length; i++) {
                const item = items[labels[i]];
                payments.data.push(item);
            }
            datasets.push(payments);

            return { labels, datasets };
        }
    },
    components: { Widget, StackedBarChart }
}
</script>

<style>
    .circle {
        display: inline-block;
        vertical-align: top;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 4px 12px;
    }

    .label {font-size:  1.2em !important;}
    .input-label.input-label {font-size:  1.02em !important;}
    
    .groupby .v-text-field__details { display: none; }
</style>